import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import {
  InflationCalculatorService, InflationResult, InflationMode, INFLATION_CONSTRAINTS
} from './inflation-calculator.service';

@Component({
  selector: 'app-inflation-calculator',
  standalone: true,
  imports: [FormsModule],
  providers: [InvestmentChartService],
  templateUrl: './inflation-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./inflation-calculator.scss']
})
export class InflationCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(InflationCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('growthRef') growthRef!: ElementRef<HTMLCanvasElement>;

  readonly C = INFLATION_CONSTRAINTS;
  readonly Math = Math;

  mode:        InflationMode = 'futureCost';
  amount = 100_000;
  annualRate = 6;
  years = 10;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     InflationResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.amount, this.C.amount.min, this.C.amount.max, 'Amount');
    if (e1) errors['amount'] = e1;
    const e2 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Inflation rate');
    if (e2) errors['annualRate'] = e2;
    const e3 = validateRange(this.years, this.C.years.min, this.C.years.max, 'Duration');
    if (e3) errors['years'] = e3;
    else if (!Number.isInteger(Number(this.years))) errors['years'] = 'Duration must be a whole number of years.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  setMode(mode: InflationMode): void {
    this.mode = mode;
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'years') this.years = Math.floor(this.years || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { amount: true, annualRate: true, years: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.amount, this.annualRate, Math.floor(this.years), this.mode);
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Yr ${row.year}`),
        [
          { label: this.mode === 'futureCost' ? 'Future Cost' : 'Purchasing Power', data: r.years.map(row => Math.round(row.value)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.mode           = 'futureCost';
    this.amount         = 100_000;
    this.annualRate     = 6;
    this.years          = 10;
    this.formErrors     = {};
    this.touched        = {};
    this.showFormErrors = false;
    this.calculated     = false;
    this.result         = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
